/**
 * PublicQuotePixCard — o bloco "Pague por Pix" da página `/o/:id`.
 *
 * Até aqui o link público mostrava a chave Pix como TEXTO, perdida no cartão de
 * Contato: o cliente abria o orçamento no celular e não tinha como pagar sem
 * copiar a chave à mão e digitar o valor. O QR existia só no PDF — ou seja,
 * dentro de um arquivo que ele precisava baixar antes.
 *
 * ─── AS REGRAS QUE ESTE COMPONENTE NÃO REIMPLEMENTA ─────────────────────────
 *
 * Ele recebe o MESMO `PDFQuoteData` que gera o PDF e delega a decisão inteira a
 * `buildPixPaymentCode` (`pdfTemplates/shared.ts`). Isso não é preciosismo: as
 * regras de quem pode ver um QR são de negócio e já custaram caro —
 *
 *   • documento pago não leva QR nenhum, nem o dinâmico nem o estático, senão o
 *     cliente que já pagou é convidado a pagar de novo e o Pix não estorna (#168);
 *   • `payment_methods = []` (recusa explícita) esconde; `null` (legado) mostra (#73);
 *   • no modo dinâmico a chave pessoal NÃO pode aparecer — seriam dois caminhos
 *     de pagamento e só um deles é conciliado (#166);
 *   • chave que o gerador não consegue codificar não vira QR morto: não vira
 *     nada (#206).
 *
 * Uma segunda implementação aqui significaria a tela do CLIENTE e o papel dele
 * discordando sobre como pagar — a divergência do #135, num lugar onde ela custa
 * o dinheiro de alguém.
 */
import { useEffect, useState } from 'react';
import { Check, Copy, QrCode } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';
import {
  buildPixPaymentCode,
  dynamicPixIsUsable,
  renderPixQrFromCode,
  type PDFQuoteData,
} from '@/lib/pdfTemplates/shared';
import { AsaasSeal } from '@/components/payments/AsaasSeal';
import { ASAAS_SHORT_DISCLOSURE } from '@/lib/asaasDisclosure';

interface PublicQuotePixCardProps {
  /** O mesmo payload que alimenta o PDF baixado por esta página. */
  data: PDFQuoteData | null;
}

export function PublicQuotePixCard({ data }: PublicQuotePixCardProps) {
  const code = data ? buildPixPaymentCode(data) : null;
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!code) {
      setQrDataUrl(null);
      return;
    }
    // O QR sai do MESMO código que o botão copia. Gerá-lo por outro caminho faria
    // o `txid` do estático (que carimba a hora) divergir, e o cliente teria dois
    // códigos que não são o mesmo pagamento.
    renderPixQrFromCode(code).then((url) => {
      if (!cancelled) setQrDataUrl(url);
    });
    return () => {
      cancelled = true;
    };
  }, [code]);

  if (!code) return null;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      toast.success('Código Pix copiado. Cole no app do seu banco.');
      setTimeout(() => setCopied(false), 2500);
    } catch {
      // `navigator.clipboard` exige contexto seguro e pode não existir no
      // WebView do cliente. O código continua VISÍVEL na tela para seleção
      // manual — por isso ele é renderizado, e não só copiável.
      toast.warning('Não foi possível copiar. Selecione o código abaixo e copie à mão.');
    }
  };

  return (
    <Card>
      <CardContent className="space-y-3 p-4">
        <div className="flex items-center gap-2">
          <QrCode className="h-4 w-4 text-primary" />
          <h2 className="font-semibold">Pague por Pix</h2>
        </div>

        <div className="flex flex-col items-center gap-3">
          {qrDataUrl ? (
            <img
              src={qrDataUrl}
              alt="QR Code Pix para pagamento"
              className="h-44 w-44 rounded-lg border bg-white p-2"
            />
          ) : (
            <div className="flex h-44 w-44 items-center justify-center rounded-lg border bg-muted text-xs text-muted-foreground">
              Gerando QR Code...
            </div>
          )}
          <p className="text-center text-xs text-muted-foreground">
            Abra o app do seu banco, escolha Pix e escaneie o código acima.
          </p>
        </div>

        <div className="space-y-2">
          <span className="text-xs text-muted-foreground">Pix copia e cola</span>
          {/* `break-all` porque o BR Code é uma string única sem espaços: sem ele
              a linha estica e vaza a página inteira em 360px (#175/#185). */}
          <p className="max-h-20 overflow-y-auto break-all rounded-lg bg-muted p-2 font-mono text-[10px] leading-relaxed">
            {code}
          </p>
          <Button variant="outline" className="w-full" onClick={handleCopy}>
            {copied ? <Check className="mr-2 h-4 w-4" /> : <Copy className="mr-2 h-4 w-4" />}
            {copied ? 'Código copiado' : 'Copiar código Pix'}
          </Button>
        </div>

        {/*
          Atribuição obrigatória (Resolução Conjunta nº 16/2025) — esta é a tela
          em que o pagamento acontece, e ela é vista por quem NÃO tem conta no
          app: o cliente do prestador.

          Só no DINÂMICO, pela mesma régua do PDF: no estático o QR é da chave do
          próprio prestador e o Asaas não está no meio, então creditá-lo seria
          uma afirmação falsa.
        */}
        {data && dynamicPixIsUsable(data) && (
          <div className="flex flex-col items-center gap-2 border-t border-border pt-3">
            <AsaasSeal />
            <p className="text-center text-[11px] leading-snug text-muted-foreground">
              {ASAAS_SHORT_DISCLOSURE}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
